import React, { useState, useEffect, useCallback } from "react";
import { messageConfigs } from "../../services/api";
import "./MessageScripts.css";

function ScriptCard({ config, initialBody }) {
  const [draft, setDraft] = useState(initialBody);
  const [savedBody, setSavedBody] = useState(initialBody);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const isDirty = draft !== savedBody;

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await messageConfigs.update(config.key, draft);
      setSavedBody(draft);
      setStatus({ type: "success", message: "Saved successfully!" });
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setStatus({
        type: "error",
        message: err.response?.data?.message || err.message || "Failed to save",
      });
    }
    setSaving(false);
  };

  return (
    <div className="script-card">
      <div className="script-card-header">
        <div className="script-card-name">{config.name}</div>
        {config.description && (
          <div className="script-card-description">{config.description}</div>
        )}
      </div>
      <textarea
        className="script-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={6}
        spellCheck={false}
      />
      <div className="script-variables-tip">
        Supported variables:&nbsp;
        <code>{"{{order_number}}"}</code>,&nbsp;
        <code>{"{{customer_name}}"}</code>,&nbsp;
        <code>{"{{order_total}}"}</code>,&nbsp;
        <code>{"{{items}}"}</code>
      </div>
      <div className="script-card-footer">
        <div className="script-status">
          {status?.type === "success" && (
            <span className="script-status-success">{status.message}</span>
          )}
          {status?.type === "error" && (
            <span className="script-status-error">{status.message}</span>
          )}
        </div>
        <button
          className="script-save-btn"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function MessageScripts() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await messageConfigs.getAll();
      setConfigs(Array.isArray(data) ? data : data.configs || []);
    } catch (err) {
      setFetchError(err.response?.data?.message || err.message || "Failed to load message scripts");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  if (loading) {
    return (
      <div className="scripts-page">
        <div className="scripts-header">
          <h1>Message Scripts</h1>
        </div>
        <div className="scripts-loading">Loading message scripts…</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="scripts-page">
        <div className="scripts-header">
          <h1>Message Scripts</h1>
        </div>
        <div className="scripts-fetch-error">
          <div className="scripts-fetch-error-msg">{fetchError}</div>
          <button className="script-save-btn" onClick={loadConfigs}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="scripts-page">
      <div className="scripts-header">
        <h1>Message Scripts</h1>
        <p className="scripts-subtitle">
          Edit the WhatsApp message templates sent to customers.
        </p>
      </div>
      {configs.length === 0 ? (
        <div className="scripts-empty">No message scripts found.</div>
      ) : (
        <div className="scripts-grid">
          {configs.map((config) => (
            <ScriptCard
              key={config.key}
              config={config}
              initialBody={config.body || ""}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default MessageScripts;
